/**
 * NotificationContext — سیستم یکپارچه پیام‌های سیستمی
 *
 * استفاده:
 *   const { showSuccess, showError, showErrors, showConfirm, showValidation, showLoading, hideLoading } = useNotification();
 *
 * برای جزئیات بیشتر: docs/notification-system.md
 */
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export type NotificationType = 'success' | 'error' | 'warning' | 'info' | 'confirm' | 'loading';

export interface ConfirmOptions {
  message: string;
  title?: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel?: () => void;
}

export interface ValidationOptions {
  errors: string[];
  warnings?: string[];
  title?: string;
  focusRef?: React.RefObject<HTMLElement | null>;
  onConfirm?: () => void;
}

export interface NotificationState {
  open: boolean;
  type: NotificationType;
  title: string;
  message: string;
  messages: string[];
  confirmText: string;
  cancelText: string;
  autoCloseDuration: number | null;
  onConfirm?: () => void;
  onCancel?: () => void;
}

interface NotificationContextValue {
  state: NotificationState;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showErrors: (errors: string[], title?: string) => void;
  showWarning: (message: string) => void;
  showWarnings: (warnings: string[], title?: string) => void;
  showValidation: (options: ValidationOptions) => void;
  showInfo: (message: string) => void;
  showConfirm: (options: ConfirmOptions) => void;
  showLoading: (message?: string) => void;
  hideLoading: () => void;
  close: () => void;
}

// ─── Default State ─────────────────────────────────────────────────────────────

const defaultState: NotificationState = {
  open: false,
  type: 'info',
  title: '',
  message: '',
  messages: [],
  confirmText: 'متوجه شدم',
  cancelText: 'انصراف',
  autoCloseDuration: null,
  onConfirm: undefined,
  onCancel: undefined,
};

const TYPE_TITLES: Record<NotificationType, string> = {
  success: 'موفقیت',
  error: 'خطا',
  warning: 'هشدار',
  info: 'اطلاعات',
  confirm: 'تأیید عملیات',
  loading: 'در حال پردازش',
};

const TYPE_AUTO_CLOSE: Record<NotificationType, number | null> = {
  success: 3000,
  error: null,
  warning: null,
  info: 5000,
  confirm: null,
  loading: null,
};

// ─── Context ──────────────────────────────────────────────────────────────────

const NotificationContext = createContext<NotificationContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<NotificationState>(defaultState);
  const autoCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = () => {
    if (autoCloseTimer.current) {
      clearTimeout(autoCloseTimer.current);
      autoCloseTimer.current = null;
    }
  };

  const close = useCallback(() => {
    clearTimer();
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  const show = useCallback(
    (partial: Partial<NotificationState> & { type: NotificationType; message: string }) => {
      clearTimer();
      const type = partial.type;
      const duration = TYPE_AUTO_CLOSE[type];
      const newState: NotificationState = {
        ...defaultState,
        ...partial,
        open: true,
        title: partial.title || TYPE_TITLES[type],
        autoCloseDuration: duration,
      };
      setState(newState);

      if (duration) {
        autoCloseTimer.current = setTimeout(() => {
          setState((prev) => ({ ...prev, open: false }));
        }, duration);
      }
    },
    []
  );

  const showSuccess = useCallback(
    (message: string) => show({ type: 'success', message, messages: [] }),
    [show]
  );

  const showError = useCallback(
    (message: string) => show({ type: 'error', message, messages: [] }),
    [show]
  );

  const showErrors = useCallback(
    (errors: string[], title?: string) =>
      show({ type: 'error', message: '', messages: errors, title: title || 'لطفاً موارد زیر را اصلاح کنید' }),
    [show]
  );

  const showWarning = useCallback(
    (message: string) => show({ type: 'warning', message, messages: [] }),
    [show]
  );

  const showWarnings = useCallback(
    (warnings: string[], title?: string) =>
      show({ type: 'warning', message: '', messages: warnings, title: title || 'هشدار' }),
    [show]
  );

  const showValidation = useCallback(
    (options: ValidationOptions) => {
      const { errors, warnings, title, focusRef, onConfirm } = options;
      const hasErrors = errors.length > 0;
      const hasWarnings = !hasErrors && warnings && warnings.length > 0;
      const allMessages = hasErrors ? errors : (warnings || []);
      const type: NotificationType = hasErrors ? 'error' : 'warning';
      const defaultTitle = hasErrors
        ? 'لطفاً موارد زیر را اصلاح کنید'
        : 'توجه داشته باشید';

      const handleClose = () => {
        if (focusRef?.current) {
          setTimeout(() => focusRef.current?.focus(), 50);
        }
      };

      if (hasWarnings && onConfirm) {
        show({
          type: 'confirm',
          message: '',
          messages: allMessages,
          title: title || 'هشدار',
          confirmText: 'بله، ادامه ده',
          cancelText: 'ویرایش فرم',
          onConfirm,
          onCancel: handleClose,
        });
      } else {
        show({
          type,
          message: '',
          messages: allMessages,
          title: title || defaultTitle,
          onConfirm: handleClose,
        });
      }
    },
    [show]
  );

  const showInfo = useCallback(
    (message: string) => show({ type: 'info', message, messages: [] }),
    [show]
  );

  const showConfirm = useCallback(
    (options: ConfirmOptions) =>
      show({
        type: 'confirm',
        message: options.message,
        messages: [],
        title: options.title || TYPE_TITLES.confirm,
        confirmText: options.confirmText || 'بله، مطمئنم',
        cancelText: options.cancelText || 'انصراف',
        onConfirm: options.onConfirm,
        onCancel: options.onCancel,
      }),
    [show]
  );

  const showLoading = useCallback(
    (message = 'در حال پردازش...') =>
      show({ type: 'loading', message, messages: [], title: TYPE_TITLES.loading }),
    [show]
  );

  const hideLoading = useCallback(() => {
    setState((prev) => (prev.type === 'loading' ? { ...prev, open: false } : prev));
  }, []);

  return (
    <NotificationContext.Provider
      value={{ state, showSuccess, showError, showErrors, showWarning, showWarnings, showValidation, showInfo, showConfirm, showLoading, hideLoading, close }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * useNotification — hook برای نمایش پیام‌های سیستمی
 *
 * @example
 * const { showSuccess, showError, showConfirm } = useNotification();
 * showSuccess('ذخیره شد');
 * showError(err?.response?.data?.message || 'خطا در ذخیره');
 * showConfirm({ message: 'حذف شود؟', onConfirm: () => handleDelete(id) });
 */
export const useNotification = (): NotificationContextValue => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotification must be used inside NotificationProvider');
  return ctx;
};

export default NotificationContext;
